import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SignatureHistory = ({ signatures, document }) => {
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const getSignatureStatus = (signature) => {
    if (signature?.verified) return 'verified';
    if (signature?.pending) return 'pending';
    if (signature?.rejected) return 'rejected';
    return 'completed';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <Icon name="ShieldCheck" size={16} className="text-success" />;
      case 'pending':
        return <Icon name="Clock" size={16} className="text-warning" />;
      case 'rejected':
        return <Icon name="XCircle" size={16} className="text-error" />;
      default:
        return <Icon name="CheckCircle" size={16} className="text-accent" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Blockchain Verified';
      case 'pending':
        return 'Verification Pending';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Signed';
    }
  };

  const handleVerifySignature = (signature) => {
    setSelectedSignature(signature);
    setShowVerificationModal(true);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString(),
      time: date?.toLocaleTimeString()
    };
  };

  const mockSignatures = [
    {
      id: 1,
      userId: 'user-1',
      userName: 'John Smith',
      userRole: 'Property Owner',
      userEmail: 'john.smith@email.com',
      timestamp: '2025-01-20T14:30:00Z',
      blockchainHash: '0xa1b2c3d4e5f6789012345678901234567890abcdef1234567890abcdef123456',
      ipAddress: '192.168.1.100',
      verified: true,
      authMethod: 'private_key',
      page: 3
    },
    {
      id: 2,
      userId: 'user-2',
      userName: 'Sarah Johnson',
      userRole: 'Legal Representative',
      userEmail: 'sarah.johnson@lawfirm.com',
      timestamp: '2025-01-20T15:45:00Z',
      blockchainHash: '0xb2c3d4e5f6789012345678901234567890abcdef1234567890abcdef1234567',
      ipAddress: '192.168.1.101',
      verified: true,
      authMethod: 'private_key',
      page: 5
    },
    {
      id: 3,
      userId: 'user-3',
      userName: 'Michael Davis',
      userRole: 'Notary Public',
      userEmail: 'michael.davis@notary.com',
      timestamp: '2025-01-21T09:15:00Z',
      blockchainHash: '0xc3d4e5f6789012345678901234567890abcdef1234567890abcdef12345678',
      ipAddress: '192.168.1.102',
      pending: true,
      authMethod: 'private_key',
      page: 8
    }
  ];

  const allSignatures = [...signatures, ...mockSignatures];

  return (
    <div className="bg-card border border-border rounded-lg h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Signature History</h3>
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Shield" size={16} />
            <span>Blockchain Secured</span>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {allSignatures?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Icon name="PenTool" size={48} className="text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No signatures yet</h4>
            <p className="text-sm text-muted-foreground">
              Signature history will appear here once stakeholders begin signing the document.
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {allSignatures?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.map((signature) => {
                const status = getSignatureStatus(signature);
                const { date, time } = formatTimestamp(signature?.timestamp);

                return (
                  <div key={signature?.id} className="border border-border rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-foreground">
                            {signature?.userName?.charAt(0)}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium text-foreground">{signature?.userName}</h4>
                            {getStatusIcon(status)}
                          </div>
                          <p className="text-sm text-muted-foreground">{signature?.userRole}</p>
                          <p className="text-xs text-muted-foreground">{signature?.userEmail}</p>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          status === 'verified' ? 'bg-success/20 text-success' :
                          status === 'pending' ? 'bg-warning/20 text-warning' :
                          status === 'rejected'? 'bg-error/20 text-error' : 'bg-accent/20 text-accent'
                        }`}>
                          {getStatusText(status)}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Signed on:</p>
                        <p className="font-medium text-foreground">{date} at {time}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Page:</p>
                        <p className="font-medium text-foreground">Page {signature?.page}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Blockchain Hash:</p>
                        <p className="text-xs font-mono text-foreground bg-muted px-2 py-1 rounded">
                          {signature?.blockchainHash}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                          <span>IP: {signature?.ipAddress}</span>
                          <span>Auth: {signature?.authMethod}</span>
                        </div>
                        
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => handleVerifySignature(signature)}
                          iconName="Search"
                          iconPosition="left"
                        >
                          Verify
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
      {/* Document Summary */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-lg font-semibold text-foreground">
              {allSignatures?.filter(s => s?.verified || (!s?.pending && !s?.rejected))?.length}
            </p>
            <p className="text-xs text-muted-foreground">Total Signatures</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-success">
              {allSignatures?.filter(s => s?.verified)?.length}
            </p>
            <p className="text-xs text-muted-foreground">Verified</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-warning">
              {allSignatures?.filter(s => s?.pending)?.length}
            </p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
        </div>
      </div>
      {/* Verification Modal */}
      {showVerificationModal && selectedSignature && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">Signature Verification</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowVerificationModal(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3 p-4 bg-success/10 border border-success/20 rounded-lg">
                  <Icon name="ShieldCheck" size={24} className="text-success" />
                  <div>
                    <p className="font-medium text-success">Signature Verified</p>
                    <p className="text-sm text-muted-foreground">
                      This signature has been verified on the blockchain
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">Signer Information</p>
                    <div className="mt-1 p-3 bg-muted/30 rounded">
                      <p className="font-medium">{selectedSignature?.userName}</p>
                      <p className="text-sm text-muted-foreground">{selectedSignature?.userRole}</p>
                      <p className="text-sm text-muted-foreground">{selectedSignature?.userEmail}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground">Timestamp</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatTimestamp(selectedSignature?.timestamp)?.date} at {formatTimestamp(selectedSignature?.timestamp)?.time}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-foreground">Blockchain Hash</p>
                    <p className="text-xs font-mono text-muted-foreground mt-1 break-all">
                      {selectedSignature?.blockchainHash}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">IP Address</p>
                      <p className="text-sm text-muted-foreground">{selectedSignature?.ipAddress}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Auth Method</p>
                      <p className="text-sm text-muted-foreground">{selectedSignature?.authMethod}</p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowVerificationModal(false)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                  <Button
                    variant="default"
                    iconName="Download"
                    iconPosition="left"
                    className="flex-1"
                  >
                    Download Certificate
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureHistory;